/**
 * Module dependencies.
 */

import { Text, baseFontSize, clampCalc } from 'src/components/core/text';
import { TourData } from 'src/types/tour';
import { forwardRef } from 'react';
import { media } from 'src/styles/media';
import ColumnsText from 'src/components/columns-text';
import Container from 'src/components/core/layout/container';
import FadeInAnimation from 'src/components/core/animations/fade-in';
import FadeInRightAnimation from 'src/components/core/animations/fade-in-right';
import FadeInUpAnimation from 'src/components/core/animations/fade-in-up';
import Image from 'src/components/core/image';
import Svg from 'src/components/core/svg';
import pinIcon from 'src/assets/svg/pin.svg';
import styled from 'styled-components';
import useBreakpoint from 'src/hooks/use-breakpoint';

/**
 * `Props` type.
 */

type Props = Partial<TourData['sections']['authorsNotes']>;

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  position: relative;

  ${media.min.md`
    background-color: var(--color-grey-100);
    height: 100vh;
  `}

  ${media.max.md`
    margin-bottom: 5.75rem;
  `}
`;

/**
 * `FirstGrid` styled component.
 */

const FirstGrid = styled.div`
  ${media.min.md`
    display: grid;
    grid-template-areas:
      'titleWrapper titleWrapper .     .'
      '.            quote        quote .';
    grid-template-columns: 3fr 2fr 2fr 1fr;
    grid-template-rows: max-content 1fr;
    padding: 9.8vw 0;
    width: 90vw;
  `}

  ${media.min.lg`
    padding: 7.3vw 0 5.4vw;
  `}

  ${media.min.xl`
    width: 80vw;
  `}

  ${media.min.xxl`
    width: 62vw;
  `}
`;

/**
 * `SecondGrid` styled component.
 */

const SecondGrid = styled.div`
  ${media.min.md`
    display: grid;
    grid-template-areas:
      'titleMapName .'
      'image        .';
    grid-template-columns: 6fr 1fr;
    grid-template-rows: max-content 1fr;
    padding: 9.8vw 0;
    width: 90vw;
  `}

  ${media.min.lg`
    padding: 7.3vw 0 5.4vw;
    width: 70vw;
  `}

  ${media.min.xxl`
    width: 55vw;
  `}
`;

/**
 * `ContainerStyle` styled component.
 */

const ContainerStyle = styled(Container)`
  ${media.min.md`
    display: flex;
    height: 100%;
    padding: 0 4.5rem;
  `}

  ${media.min.xxl`
    display: flex;
    height: 100%;
    padding: 0 calc((100vw - var(--container-max-width)) / 2 + var(--gutter));
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Text)`
  font-size: clamp(${41 / baseFontSize}rem, ${clampCalc(41, 80)}, ${80 / baseFontSize}rem);
  letter-spacing: clamp(${2 / baseFontSize}rem, ${clampCalc(2, 4)}, ${4 / baseFontSize}rem);
  line-height: clamp(${32 / baseFontSize}rem, ${clampCalc(32, 73)}, ${73 / baseFontSize}rem);
  margin: 0;
  position: relative;
  text-transform: uppercase;
  z-index: 2;

  ${media.max.md`
    text-align: center;
  `}
`;

/**
 * `TextHandWritten` styled component.
 */

const TextHandWritten = styled(Text)`
  color: var(--color-yellow);
  display: block;
  margin: 1.25rem 3rem 0 0;
  text-align: right;
  text-transform: none;

  ${media.min.md`
    margin: 1.25rem 0 0;
  `}

  @supports (-webkit-hyphens: none) {
    font-size: clamp(36px, ${clampCalc(36, 90)}, 90px);

    ${media.min.md`
      margin: 1.25rem 3rem 0 0;
    `}
  }
`;

/**
 * `QuoteWrapper` styled component.
 */

const QuoteWrapper = styled.div`
  margin: 8rem 0 9rem;

  ${media.min.md`
    grid-area: quote;
    align-self: center;
    margin: 5rem 0 0;
 `}

  ${media.max.md`
    margin-bottom: 9rem;
    text-align: center;
  `}
`;

/**
 * `TitleWrapper` styled component.
 */

const TitleWrapper = styled.div`
  position: relative;

  ${media.min.md`
    grid-area: titleWrapper;
  `}
`;

/**
 * `SmallSvg` styled component.
 */

const SmallSvg = styled(Svg)`
  margin-left: 1rem;

  ${media.min.md`
    margin-left: 10%;
  `}
`;

/**
 * `MediumSvg` styled component.
 */

const MediumSvg = styled(Svg)`
  position: absolute;
  right: 0;
  top: 3rem;
  z-index: 1;

  ${media.min.md`
    right: -8.25rem;
    top: 8.5rem;
 `}
`;

/**
 * `BigSvg` styled component.
 */

const BigSvg = styled(Svg)`
  left: 0;
  position: absolute;
  top: 3.5rem;
  z-index: 1;

  ${media.min.md`
    top: 9.875rem;
  `}

  ${media.min.lg`
    left: 4.25rem;
  `}
`;

/**
 * `TitleMapNameWrapper` styled component.
 */

const TitleMapNameWrapper = styled.div`
  margin: 0 0 1.25rem;

  ${media.min.md`
    grid-area: titleMapName;
    margin: 2.5rem 0;
  `}
`;

/**
 * `TitleMapName` styled component.
 */

const TitleMapName = styled(Text)`
  margin: 0;
  text-transform: uppercase;

  ${media.max.md`
    text-align: center;
  `}

  ${media.max.sm`
    font-size: 2.6rem;
    letter-spacing: 0.078125rem;
    line-height: 1;
  `}
`;

/**
 * `ImageMapNameWrapper` styled component.
 */

const ImageMapNameWrapper = styled.div`
  ${media.min.md`
    grid-area: image;
  `}

  ${media.max.md`
    margin-bottom: 3.25rem;
 `}
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  height: 100%;
  overflow: hidden;
  position: relative;

  ${media.max.md`
    padding-bottom: 69.96%;
    margin-bottom: 3.25rem;
  `}
`;

/**
 * `StyledColumnsText` styled component.
 */

const StyledColumnsText = styled.div`
  ${media.min.md`
    margin-bottom: 2rem;
    padding: 9.8vw 0;
  `}

  ${media.min.lg`
    padding: 7.3vw 0 5.4vw;
  `}
`;

/**
 * `AuthorsNotesSection` component.
 */

const AuthorsNotesSection = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const { author, image, mapName, quote, subtitle, text, title } = props;
  const isMobile = useBreakpoint('md', 'max');
  const authorText = `${text}<br><span>${author}</span>`;

  return (
    <Wrapper ref={ref}>
      <ContainerStyle fluid>
        <FirstGrid>
          <TitleWrapper>
            <FadeInAnimation
              options={{
                transitionDelay: 0.5,
                transitionTime: 1
              }}
            >
              <SmallSvg icon={pinIcon} size={isMobile ? '1.75rem' : '4rem'} />
            </FadeInAnimation>

            {!isMobile && (
              <FadeInRightAnimation options={{ transitionTime: 1 }}>
                <Title as={'h2'} variant={'boldM'}>
                  {title}

                  <TextHandWritten as={'span'} variant={'handwrittenL'}>
                    {subtitle}
                  </TextHandWritten>
                </Title>
              </FadeInRightAnimation>
            )}

            {isMobile && (
              <FadeInUpAnimation options={{ transitionTime: 1 }}>
                <Title as={'h2'} variant={'boldM'}>
                  {title}

                  <TextHandWritten as={'span'} variant={'handwrittenL'}>
                    {subtitle}
                  </TextHandWritten>
                </Title>
              </FadeInUpAnimation>
            )}

            <FadeInAnimation
              options={{
                transitionDelay: 0.6,
                transitionTime: 1
              }}
            >
              <MediumSvg icon={pinIcon} size={isMobile ? '2.25rem' : '5.25rem'} />
            </FadeInAnimation>

            <FadeInAnimation
              options={{
                transitionDelay: 0.7,
                transitionTime: 1
              }}
            >
              <BigSvg icon={pinIcon} size={isMobile ? '4.625rem' : '10.5rem'} />
            </FadeInAnimation>
          </TitleWrapper>

          <QuoteWrapper>
            {!isMobile && (
              <FadeInRightAnimation options={{ transitionTime: 1 }}>
                <Text as={'p'} variant={'quoteBlock'}>
                  {quote}
                </Text>
              </FadeInRightAnimation>
            )}

            {isMobile && (
              <FadeInUpAnimation options={{ transitionTime: 1 }}>
                <Text as={'p'} variant={'quoteBlock'}>
                  {quote}
                </Text>
              </FadeInUpAnimation>
            )}
          </QuoteWrapper>
        </FirstGrid>

        <SecondGrid>
          <TitleMapNameWrapper>
            {!isMobile && (
              <FadeInRightAnimation
                options={{
                  threshold: 0.2,
                  transitionTime: 1
                }}
              >
                <TitleMapName as={'h2'} variant={'boldM'}>
                  {mapName?.replaceAll('-', '-⁠')}
                </TitleMapName>
              </FadeInRightAnimation>
            )}

            {isMobile && (
              <FadeInUpAnimation options={{ transitionTime: 1 }}>
                <TitleMapName as={'h2'} variant={'boldL'}>
                  {mapName}
                </TitleMapName>
              </FadeInUpAnimation>
            )}
          </TitleMapNameWrapper>

          <ImageMapNameWrapper>
            {image && (
              <FadeInAnimation fullHeight options={{ transitionTime: 1 }}>
                <ImageWrapper>
                  <Image alt={mapName} layout={'fill'} objectFit={'contain'} objectPosition={'top left'} src={image} />
                </ImageWrapper>
              </FadeInAnimation>
            )}
          </ImageMapNameWrapper>
        </SecondGrid>

        {!isMobile && (
          <StyledColumnsText>
            <FadeInRightAnimation
              fullHeight
              options={{
                threshold: 0.1,
                transitionTime: 1
              }}
            >
              <ColumnsText text={authorText} />
            </FadeInRightAnimation>
          </StyledColumnsText>
        )}

        {isMobile && (
          <StyledColumnsText>
            <ColumnsText text={authorText} />
          </StyledColumnsText>
        )}
      </ContainerStyle>
    </Wrapper>
  );
});

AuthorsNotesSection.displayName = 'AuthorsNotesSection';

/**
 * Export `AuthorsNotesSection` component.
 */

export default AuthorsNotesSection;
